// client/src/components/media/ExternalUploadPage.jsx
// Public, unauthenticated upload page reached via a 24h tenant-issued link.
// Intentionally renders WITHOUT AppHeader / Sidebar / AppFooter.
//
// Growth Phase 9: the same page serves an attendee's own photo link
// (meta.purpose 'attendee_photos', emailed in the post-event thank-you).
// Then it is photos only, mobile-first (camera roll picker), photos are
// shrunk in the browser before upload (utils/resizeImage.js), and the
// "you may repost these" box must be ticked. Uploads wait for staff review.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  FaCloudUploadAlt,
  FaImage,
  FaVideo,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimes,
} from "react-icons/fa";
import "./ExternalUploadPage.css";
import { resizeAll } from "../../utils/resizeImage";

const BASE_URL =
  process.env.REACT_APP_BASE_URL ||
  (typeof window !== "undefined" ? window.location.origin : "");

const STATE_LOADING = "loading";
const STATE_READY = "ready";
const STATE_EXPIRED = "expired";
const STATE_NOT_FOUND = "not_found";
const STATE_QUOTA = "quota_exceeded";
const STATE_ERROR = "error";

function resolveLogoUrl(logoUrl) {
  if (!logoUrl) return null;
  if (/^https?:\/\//i.test(logoUrl)) return logoUrl;
  return `${BASE_URL}${logoUrl.startsWith("/") ? "" : "/"}${logoUrl}`;
}

function formatExpiresAt(iso, locale) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(locale || undefined);
  } catch (_) {
    return iso;
  }
}

const ExternalUploadPage = () => {
  const { t, i18n } = useTranslation();
  const { token } = useParams();
  const [pageState, setPageState] = useState(STATE_LOADING);
  const [meta, setMeta] = useState(null);
  const [selected, setSelected] = useState([]); // File[]
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null); // response.results
  const [remainingUploads, setRemainingUploads] = useState(null);
  const [error, setError] = useState("");
  const [consent, setConsent] = useState(false);
  const [name, setName] = useState("");
  const [preparing, setPreparing] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BASE_URL}/api/external-upload/${token}`);
        if (cancelled) return;
        setMeta(data);
        setRemainingUploads(data.remainingUploads);
        setPageState(STATE_READY);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) setPageState(STATE_NOT_FOUND);
        else if (status === 410) setPageState(STATE_EXPIRED);
        else if (status === 429) setPageState(STATE_QUOTA);
        else setPageState(STATE_ERROR);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const attendee = meta?.purpose === "attendee_photos";
  const maxFiles = meta?.maxFilesPerRequest || 12;
  const maxBytes = meta?.maxFileBytes || 50 * 1024 * 1024;
  const maxMB = Math.round(maxBytes / (1024 * 1024));

  const acceptFiles = useCallback(
    (fileList) => {
      let arr = Array.from(fileList || []);
      // Attendee photo links take photos only.
      if (attendee) arr = arr.filter((f) => (f.type || "").startsWith("image/"));
      const cap = Math.min(
        maxFiles,
        remainingUploads != null ? remainingUploads : maxFiles
      );
      if (arr.length > cap) {
        return arr.slice(0, cap);
      }
      return arr;
    },
    [maxFiles, remainingUploads, attendee]
  );

  const onDrop = (e) => {
    e.preventDefault();
    if (uploading) return;
    const files = acceptFiles(e.dataTransfer.files);
    setSelected(files);
  };

  const onFileInput = (e) => {
    const files = acceptFiles(e.target.files);
    setSelected(files);
  };

  const removeAt = (idx) => {
    setSelected((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (!selected.length || uploading) return;
    if (attendee && !consent) {
      setError(t("externalUpload.attendee.consentNeeded"));
      return;
    }
    setUploading(true);
    setError("");
    setResults(null);
    try {
      const form = new FormData();
      // Text fields first so they're parsed before the files stream in.
      if (attendee) {
        form.append("consentToRepost", consent ? "1" : "0");
        if (name.trim()) form.append("name", name.trim());
      }
      let files = selected;
      if (attendee) {
        setPreparing(true);
        files = await resizeAll(selected);
        setPreparing(false);
      }
      files.forEach((f) => form.append("media", f, f.name));
      const { data } = await axios.post(
        `${BASE_URL}/api/external-upload/${token}/upload`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setResults(data.results || []);
      if (typeof data.remainingUploads === "number") {
        setRemainingUploads(data.remainingUploads);
      }
      setSelected([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.error;
      setPreparing(false);
      if (status === 410) setPageState(STATE_EXPIRED);
      else if (status === 404) setPageState(STATE_NOT_FOUND);
      else if (status === 429) {
        if (code === "quota_exceeded") setPageState(STATE_QUOTA);
        else setError(t(`externalUpload.fileError.${code || "generic"}`, { defaultValue: code || "error" }));
      } else {
        setError(
          t(`externalUpload.fileError.${code || "generic"}`, {
            defaultValue: code || t("events.externalUpload.error.generic"),
          })
        );
      }
    } finally {
      setUploading(false);
    }
  };

  const addMore = () => {
    setResults(null);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // --- Render states ---------------------------------------------------------

  if (pageState === STATE_LOADING) {
    return (
      <div className="ext-upload-page">
        <div className="ext-upload-card">
          <p>{t("externalUpload.uploading", { defaultValue: "Loading…" })}</p>
        </div>
      </div>
    );
  }

  if (pageState === STATE_EXPIRED) {
    return (
      <div className="ext-upload-page">
        <div className="ext-upload-card ext-upload-empty">
          <FaExclamationTriangle size={36} style={{ color: "#c78a00" }} />
          <h1>{t("externalUpload.state.expired")}</h1>
        </div>
      </div>
    );
  }

  if (pageState === STATE_NOT_FOUND) {
    return (
      <div className="ext-upload-page">
        <div className="ext-upload-card ext-upload-empty">
          <FaExclamationTriangle size={36} style={{ color: "#b00" }} />
          <h1>{t("externalUpload.state.notFound")}</h1>
        </div>
      </div>
    );
  }

  if (pageState === STATE_QUOTA) {
    return (
      <div className="ext-upload-page">
        <div className="ext-upload-card ext-upload-empty">
          <FaExclamationTriangle size={36} style={{ color: "#c78a00" }} />
          <h1>{t("externalUpload.state.quotaExceeded")}</h1>
        </div>
      </div>
    );
  }

  if (pageState === STATE_ERROR) {
    return (
      <div className="ext-upload-page">
        <div className="ext-upload-card ext-upload-empty">
          <FaExclamationTriangle size={36} style={{ color: "#b00" }} />
          <h1>{t("events.externalUpload.error.generic")}</h1>
        </div>
      </div>
    );
  }

  const tenantName = meta?.tenant?.name || "";
  const logoUrl = resolveLogoUrl(meta?.tenant?.logoUrl);
  const eventName = meta?.event?.name || "";
  const itemName = meta?.item?.name || "";
  const subheading = itemName
    ? t("externalUpload.subheadingItem", { eventName, itemName })
    : t("externalUpload.subheading", { eventName });

  return (
    <div className="ext-upload-page">
      <div className="ext-upload-card">
        <header className="ext-upload-header">
          {logoUrl && (
            <img src={logoUrl} alt={tenantName} className="ext-upload-logo" />
          )}
          <div>
            <div className="ext-upload-tenant">{tenantName}</div>
            <h1 className="ext-upload-title">
              {attendee ? t("externalUpload.attendee.title") : t("externalUpload.pageTitle")}
            </h1>
            <div className="ext-upload-sub">{subheading}</div>
            {attendee && meta?.attendeeName && (
              <div className="ext-upload-sub">
                {t("externalUpload.attendee.hello", { name: String(meta.attendeeName).split(/\s+/)[0] })}
              </div>
            )}
          </div>
        </header>

        <div className="ext-upload-meta">
          <span>
            {t("externalUpload.expiresAt", {
              date: formatExpiresAt(meta?.expiresAt, i18n.language),
            })}
          </span>
          <span>
            {t("externalUpload.remaining", {
              count: remainingUploads ?? 0,
              defaultValue: `${remainingUploads ?? 0} remaining`,
            })}
          </span>
        </div>

        {!results && (
          <>
            <label
              htmlFor="ext-upload-input"
              className={`ext-upload-drop ${uploading ? "is-disabled" : ""}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
            >
              <FaCloudUploadAlt size={36} />
              <div>{attendee ? t("externalUpload.attendee.pick") : t("externalUpload.dropZone")}</div>
              <small>
                {t("externalUpload.limits", {
                  maxFiles,
                  maxMB,
                  defaultValue: `Up to ${maxFiles} files, ${maxMB} MB each`,
                })}
              </small>
            </label>
            <input
              id="ext-upload-input"
              ref={fileInputRef}
              type="file"
              accept={attendee ? "image/*" : "image/*,video/*"}
              multiple
              onChange={onFileInput}
              style={{ display: "none" }}
            />

            {selected.length > 0 && (
              <ul className="ext-upload-list">
                {selected.map((f, i) => (
                  <li key={`${f.name}-${i}`}>
                    {f.type.startsWith("video/") ? <FaVideo /> : <FaImage />}
                    <span className="ext-upload-file-name">{f.name}</span>
                    <span className="ext-upload-file-size">
                      {(f.size / (1024 * 1024)).toFixed(1)} MB
                    </span>
                    <button
                      type="button"
                      className="ext-upload-remove"
                      onClick={() => removeAt(i)}
                      disabled={uploading}
                      aria-label="Remove"
                    >
                      <FaTimes />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {attendee && (
              <div className="ext-upload-attendee">
                <label className="ext-upload-label" htmlFor="ext-upload-name">
                  {t("externalUpload.attendee.nameLabel")}
                </label>
                <input
                  id="ext-upload-name"
                  className="ext-upload-input"
                  type="text"
                  maxLength={120}
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <label className="ext-upload-consent">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                  />
                  <span>{t("externalUpload.attendee.consent", { tenant: tenantName })}</span>
                </label>
                <p className="ext-upload-note">{t("externalUpload.attendee.review")}</p>
              </div>
            )}

            {error && <div className="ext-upload-error">{error}</div>}

            <button
              type="button"
              className="ext-upload-submit"
              onClick={submit}
              disabled={!selected.length || uploading || (attendee && !consent)}
            >
              {preparing
                ? t("externalUpload.attendee.preparing")
                : uploading ? t("externalUpload.uploading") : t("externalUpload.submit")}
            </button>
          </>
        )}

        {results && (
          <div className="ext-upload-results">
            <h2>{t("externalUpload.done")}</h2>
            {attendee && <p className="ext-upload-note">{t("externalUpload.attendee.thanks")}</p>}
            <ul>
              {results.map((r, i) => (
                <li
                  key={`${r.filename}-${i}`}
                  className={r.error ? "is-error" : "is-ok"}
                >
                  {r.error ? (
                    <FaExclamationTriangle style={{ color: "#b00" }} />
                  ) : (
                    <FaCheckCircle style={{ color: "#1a9e4b" }} />
                  )}
                  <span className="ext-upload-file-name">{r.filename}</span>
                  {r.error && (
                    <span className="ext-upload-file-err">
                      {t(`externalUpload.fileError.${r.error}`, {
                        defaultValue: r.error,
                      })}
                    </span>
                  )}
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="ext-upload-submit"
              onClick={addMore}
              disabled={remainingUploads === 0}
            >
              {t("externalUpload.addMore")}
            </button>
          </div>
        )}

        <footer className="ext-upload-footer">
          <small>Powered by OnScreen</small>
        </footer>
      </div>
    </div>
  );
};

export default ExternalUploadPage;
